import React, { useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import cx from 'classnames';

import { MDText } from 'i18n-react';

import { ULEZ_CHECKER_EVENTS } from 'Utilities/analytics';

import LocalTexts from 'Containers/app/contentPages/ulezPage/ulezText.json';
import VrmErrors from 'Containers/app/contentPages/ulezPage/VrmErrors/VrmErrors';

import VrmChecker, { VrmCheckerBaseProps } from '../VrmChecker/VrmChecker';

import useHandleVRMUlezSubmit from './useHandleVRMUlezSubmit';

import styles from './VrmUlezChecker.scss';

const LocalT = new MDText(LocalTexts);

type VRMUlezCheckerProps = {
  hideErrors?: boolean;
  inFooter?: boolean;
}

type LocationState = {
  validVrm?: boolean;
  vrm?: string;
}

const VrmUlezChecker = ({ hideErrors = false, inFooter = false }: VRMUlezCheckerProps) => {
  const gaEventLabel = inFooter ? 'ULEZ VRM Footer' : 'Hero';
  const snowplowEventLabel = inFooter ? 'footer' : 'hero';

  const [isValidVrm, setIsValidVrm] = useState(true);
  const [submittedVrm, setSubmittedVrm] = useState<LocationState['vrm']>();

  const { disabled, errorMessage, handleVRMUlezSubmit } = useHandleVRMUlezSubmit({ gaEventLabel });

  const history = useHistory();
  const location = useLocation<LocationState>();

  const label = LocalT.translate(hideErrors ? 'vrmBox.widgetTitle' : 'vrmBox.title');
  const ctaLabel = LocalT.translate(isValidVrm ? 'vrmBox.buttonLabel' : 'vrmBox.error.buttonLabel');

  useEffect(() => {
    const { validVrm, vrm } = location?.state || {};

    if (validVrm !== undefined) {
      setIsValidVrm(validVrm);
      history.replace(location.pathname, null);
    }
    if (vrm) {
      setSubmittedVrm(vrm);
    }
  }, [location, history]);

  const vrmLabel = (
    inFooter
      ? <p data-lookup-title className="mw-i">{label}</p>
      : <h3 className={styles.vrmLabel}>{label}</h3>
  );

  const content = (
    <>
      {
        isValidVrm || hideErrors
          ? vrmLabel
          : (
            <div className={styles.errorMessage}>
              <VrmErrors error={errorMessage} />
            </div>
          )
      }
      <VrmChecker
        chevron
        large
        analyticsEvents={{
          onButtonClick: () => ULEZ_CHECKER_EVENTS.VRM_CTA_CLICKED(snowplowEventLabel, String(ctaLabel)),
          onInputFocus: () => ULEZ_CHECKER_EVENTS.VRM_INPUT_FOCUSED(snowplowEventLabel),
        }}
        buttonProps={{
          className: styles.ulezButton,
          label: ctaLabel,
        }}
        className={styles.ulezChecker}
        data-cy="ulezVRM"
        disabled={disabled}
        handleVrmSubmit={handleVRMUlezSubmit}
        parentKey="ulez-vrm"
        value={submittedVrm as VrmCheckerBaseProps['value']}
      />
    </>
  );

  return (
    inFooter
      ? content
      : <aside className={cx(styles.vrmBox, { [styles.error]: !isValidVrm })}>{content}</aside>
  );
};

export default VrmUlezChecker;
